import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getWorkspaces } from "../api";
import { useAuth } from "./AuthContext";

const WorkspaceContext = createContext(null);

const WORKSPACE_ID_STORAGE_KEY = "workspaceId";
const WORKSPACE_USER_STORAGE_KEY = "workspaceUserId";
const WORKSPACE_SOURCE_STORAGE_KEY = "workspaceSelectionSource";

const normalizeWorkspaces = (payload) => (Array.isArray(payload) ? payload : []);
const getStoredWorkspaceId = (userId = "") => {
  if (typeof window === "undefined") return "";
  const storedId = String(window.localStorage.getItem(WORKSPACE_ID_STORAGE_KEY) || "").trim();
  if (!storedId) return "";
  const expectedUserId = String(userId || "").trim();
  const storedUserId = String(window.localStorage.getItem(WORKSPACE_USER_STORAGE_KEY) || "").trim();
  if (expectedUserId && storedUserId && storedUserId !== expectedUserId) {
    return "";
  }
  return storedId;
};

const getStoredWorkspaceSource = () => {
  if (typeof window === "undefined") return "";
  return String(window.localStorage.getItem(WORKSPACE_SOURCE_STORAGE_KEY) || "").trim();
};

const persistWorkspaceId = (id, { userId = "", source = "manual" } = {}) => {
  if (typeof window === "undefined") return;
  const nextId = String(id || "").trim();
  if (nextId) {
    window.localStorage.setItem(WORKSPACE_ID_STORAGE_KEY, nextId);
    window.localStorage.setItem(WORKSPACE_SOURCE_STORAGE_KEY, source);
    const nextUserId = String(userId || "").trim();
    if (nextUserId) {
      window.localStorage.setItem(WORKSPACE_USER_STORAGE_KEY, nextUserId);
    }
  } else {
    window.localStorage.removeItem(WORKSPACE_ID_STORAGE_KEY);
    window.localStorage.removeItem(WORKSPACE_USER_STORAGE_KEY);
    window.localStorage.removeItem(WORKSPACE_SOURCE_STORAGE_KEY);
  }
};

export const WorkspaceProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const [workspaceId, setWorkspaceId] = useState(getStoredWorkspaceId);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);

  const selectWorkspace = useCallback((id) => {
    const nextId = String(id || "").trim();
    persistWorkspaceId(nextId, { userId: user?.id, source: "manual" });
    setWorkspaceId(nextId);
  }, [user?.id]);

  const resolveDefaultWorkspaceId = useCallback(
    (rows) => {
      const list = normalizeWorkspaces(rows);
      if (!list.length) return "";

      const userId = String(user?.id || "");
      const storedId = getStoredWorkspaceId(userId);
      const hasStoredWorkspace = storedId && list.some((workspace) => String(workspace?.id || "") === storedId);
      if (hasStoredWorkspace && getStoredWorkspaceSource() === "manual") {
        return storedId;
      }

      const userDefaultWorkspaceId = String(user?.default_workspace_id || user?.defaultWorkspaceId || "").trim();
      const defaultWorkspace =
        list.find((workspace) => userDefaultWorkspaceId && String(workspace?.id || "") === userDefaultWorkspaceId) ||
        list.find((workspace) => Boolean(workspace?.is_default || workspace?.default || workspace?.isDefault)) ||
        list.find((workspace) => userId && String(workspace?.owner_id || "") === userId) ||
        list[0];

      return String(defaultWorkspace?.id || storedId || "").trim();
    },
    [user?.defaultWorkspaceId, user?.default_workspace_id, user?.id]
  );

  const refreshWorkspaces = useCallback(async () => {
    if (loading || !user) {
      setWorkspaces([]);
      setWorkspaceId("");
      setWorkspaceLoading(false);
      return [];
    }

    try {
      setWorkspaceLoading(true);
      const response = await getWorkspaces();
      const rows = normalizeWorkspaces(response.data);
      setWorkspaces(rows);
      const nextWorkspaceId = resolveDefaultWorkspaceId(rows);
      if (nextWorkspaceId !== getStoredWorkspaceId(user.id)) {
        persistWorkspaceId(nextWorkspaceId, { userId: user.id, source: "auto" });
      }
      setWorkspaceId(nextWorkspaceId);
      return rows;
    } catch {
      setWorkspaces([]);
      return [];
    } finally {
      setWorkspaceLoading(false);
    }
  }, [loading, resolveDefaultWorkspaceId, user]);

  const upsertWorkspace = useCallback((workspace) => {
    if (!workspace?.id) return;
    setWorkspaces((current) => {
      const id = String(workspace.id);
      const exists = current.some((row) => String(row.id) === id);
      if (exists) {
        return current.map((row) => (String(row.id) === id ? { ...row, ...workspace } : row));
      }
      return [workspace, ...current];
    });
  }, []);

  const removeWorkspace = useCallback((id) => {
    const targetId = String(id || "");
    if (!targetId) return;
    setWorkspaces((current) => current.filter((row) => String(row.id) !== targetId));
  }, []);

  useEffect(() => {
    if (loading || !user) {
      setWorkspaces([]);
      setWorkspaceLoading(false);
      return undefined;
    }

    refreshWorkspaces();
    const handleWorkspaceChange = () => {
      refreshWorkspaces();
    };
    window.addEventListener("advahini:workspaces-changed", handleWorkspaceChange);
    return () => {
      window.removeEventListener("advahini:workspaces-changed", handleWorkspaceChange);
    };
  }, [loading, refreshWorkspaces, user]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaceId,
        workspaces,
        workspaceLoading,
        selectWorkspace,
        refreshWorkspaces,
        upsertWorkspace,
        removeWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  return useContext(WorkspaceContext);
};
